import type {
	BoInfo,
	BusinessObjectTypeType,
	BusinessObject,
	PrimitiveType,
} from '.'

export const LanguageSwitcherTypes = {
	pipeSeparated: 'Pipe-separated texts ("en | de")',
	dropdown: 'Dropdown',
	globe: 'Globe icon',
} as const
export type LanguageSwitcherType = keyof typeof LanguageSwitcherTypes

export const LanguageSwitcherTexts = {
	shortNames: 'Short names ("en")',
	displayNames: 'Display names ("English")',
	flags: 'Flags',
	flagsAndShortNames: 'Flags and short names',
	flagsAndDisplayNames: 'Flags and display names',
} as const
export type LanguageSwitcherText = keyof typeof LanguageSwitcherTexts

export const TextBlockTypeNames = {
	text: 'Normal text',
	html: 'HTML',
	markdown: 'Markdown',
	handlebars: 'Handlebars Template',
} as const
export type TextBlockType = keyof typeof TextBlockTypeNames
export const TextBlockStyleNames = {
	div: 'Block (div)',
	p: 'Paragraphs (p)',
	span: 'Normal text (span)',
	h1: 'Heading 1 (h1)',
	h2: 'Heading 2 (h2)',
	h3: 'Heading 3 (h3)',
	h4: 'Heading 4 (h4)',
	h5: 'Heading 5 (h5)',
	h6: 'Heading 6 (h6)',
} as const
export type TextBlockStyle = keyof typeof TextBlockStyleNames
export const HtmlModes = {
	none: 'None',
	safe: 'Safe HTML only',
	unsafe: 'Unsafe HTML (DANGEROUS!)',
} as const
export type HtmlMode = keyof typeof HtmlModes

export const IdbDbName = 'lowgile'
export const IdbDefaultStoreName = 'lowgile'
export const IdbRefreshTokenProperty = 'Lowgile_refresh_token'
export const IdbUserProperty = 'Lowgile_user'
export const IdbLoginExpiresAt = 'Lowgile_login_expires_at'

export const SessionCookieName = 'Lowgile_session'
export const CsrfHeaderName = 'x-lowgile-csrf'

export const ScreenQualifiedNameAttribute = 'data-defined-in'

export const ScreenSizes = [
	'XSmall',
	'Small',
	'Medium',
	'Large',
	'XLarge',
] as const // do NOT reorder, LayoutService depends on it!
export type ScreenSize = (typeof ScreenSizes)[number]

export const BoInfoSymbol = Symbol.for('LowgileEntityInfo')
export interface HasBoInfo<
	T extends BusinessObjectTypeType = BusinessObjectTypeType,
> {
	readonly [BoInfoSymbol]: BoInfo<T>
}
export function hasBoInfo<T extends BusinessObjectTypeType>(
	bo: any,
	type?: T,
): bo is HasBoInfo<T> {
	if (bo?.[BoInfoSymbol]) {
		if (type) {
			return bo[BoInfoSymbol].boType === type
		}
		return true
	}
	return false
}

export function isBo(bo: any): bo is BusinessObject {
	const asBo = bo as BusinessObject
	return !!(asBo?.moduleId && asBo.boId && asBo.__type)
}

export const ReportViewAggregationOperators = [
	'sum',
	'avg',
	'min',
	'max',
	'count',
	'distinct',
] as const
export type ReportViewAggregationOperator =
	(typeof ReportViewAggregationOperators)[number]

export const ReportViewFilterOperators = [
	'eq',
	'neq',
	'gt',
	'gte',
	'lt',
	'lte',
	'like',
	'not like',
	'in',
	'not in',
	'between',
	'not between',
	'is null',
	'is not null',
] as const
export type ReportViewFilterOperator =
	(typeof ReportViewFilterOperators)[number]

export const HasChangedInternallySymbol = Symbol('SignalsWighGivenValue')

export const BranchNameHeaderName = 'lowgile-branchname' // must be lower-case
export const LanguageHeaderName = 'lowgile-language' // must be lower-case

export const TypeIcons: Record<PrimitiveType | BusinessObjectTypeType, string> =
	{
		'': '[-]',
		void: '[-]',
		string: '[abc]',
		number: '[#]',
		boolean: '[B]',
		Date: '[D]',
		ClientDataStore: '[CDS]',
		Entity: '[E]',
		StaticEntity: '[SE]',
		Screen: '[S]',
		Process: '[P]',
		ConfigurationItem: '[CI]',
		Label: '[L]',
		Module: '[M]',
		Role: '[R]',
		Permission: '[Per]',
		DataType: '[DT]',
		Report: '[RPT]',
		ServerDataStore: '[SDS]',
		ServiceIntegration: '[SI]',
		ServiceProvider: '[SP]',
		Resource: '[RES]',
		Language: '[LNG]',
		AccessControl: '[AC]',
		Trigger: '[TR]',
		Theme: '[T]',
		UserDefinedConfiguration: '[UDC]',
	}

export const PrimitiveConstructorNames = {
	number: 'Number',
	string: 'String',
	boolean: 'Boolean',
	Date: 'Date',
	ArrayBuffer: 'ArrayBuffer',
	Buffer: 'Buffer',
	BigInt: 'BigInt',
	Uint8Array: 'Uint8Array',
	Int8Array: 'Int8Array',
	Uint16Array: 'Uint16Array',
	Int16Array: 'Int16Array',
	Uint32Array: 'Uint32Array',
	Int32Array: 'Int32Array',
	Float32Array: 'Float32Array',
	Float64Array: 'Float64Array',
	BigInt64Array: 'BigInt64Array',
	BigUint64Array: 'BigUint64Array',
	Uint8ClampedArray: 'Uint8ClampedArray',
	Array: 'Array',
	Object: 'Object',
} as const

export const PrimitiveConstructors = {
	number: Number,
	string: String,
	boolean: Boolean,
	Date: Date,
} as const
